import React, { useEffect, useState, Fragment, useRef } from 'react';
import PropTypes from 'prop-types';
import { findIndex, find } from 'lodash';
import { Icon } from 'legacy-packages/ft-cb';
import { qbPathQuestions } from 'legacy-src/constants/paths';
import './QuestionNavigation.scss';
import Button from 'chameleon/ui-stack/ui/base/button';
import { typeSetOnLearnosityItem } from 'chameleon-src/utility/utils';

const QuestionNavigation = ({
  learnosityData,
  hasMorePages,
  itemsPerPage,
  currentPage,
  location,
  items,
  total,
  router,
  params,
  onItemChange = () => {},
  onUrlUpdate = () => {},
  pathname,
  loadMoreFF,
}) => {
  const { app } = learnosityData;
  const itemObject = app.items();
  const [learnosityItems, setLearnosityItems] = useState([]);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(-1);
  const [pageCount, setPageCount] = useState('');
  const [isNavDisabled, setIsNavDisabled] = useState(true);
  const hasMoreItemsLoaded = useRef(null);
  const [navState, setNavState] = useState({
    start: 0,
    end: 0,
  });

  useEffect(() => {
    const onItemLoad = () => {
      const clickedItemRef = getInitialItemRefFromRoute();
      const clickedItemIndex = getIndexByReference(clickedItemRef);
      setIsNavDisabled(false);
      // we want to load more items only when the item user clicked at loaded, but we don't need them te be loaded twice as
      // addItems triggers item:load event as well, use ref to avoid component re-rendering to prevent double loading of items
      if (
        !hasMoreItemsLoaded.current &&
        loadMoreFF &&
        clickedItemIndex >= 0 &&
        learnosityItems.length
      ) {
        preloadItems(learnosityItems, clickedItemIndex);
        hasMoreItemsLoaded.current = true;
      }
      typeSetOnLearnosityItem(app.getCurrentItem()?.reference);
    };
    app.once('item:load', onItemLoad);
  }, [learnosityItems, currentPreviewIndex]);

  useEffect(() => {
    const cleanItems = items.map((obj) => obj.learnosity_item_reference);
    setLearnosityItems(cleanItems);
  }, [items]);

  useEffect(() => {
    const clickedItemRef = getInitialItemRefFromRoute();
    const clickedItemIndex = getIndexByReference(clickedItemRef);
    setCurrentPreviewIndex(clickedItemIndex);
  }, [learnosityItems]);

  useEffect(() => {
    if (currentPreviewIndex > -1) {
      const reference = learnosityItems[currentPreviewIndex];
      calculatePageCount();
      updateRouteUrl(reference);
    }
  }, [currentPreviewIndex]);

  // preload more items after we load the first one, based on the number in the loadMoreFF
  const preloadItems = (refs, currentItemIndex) => {
    const start =
      currentItemIndex - loadMoreFF >= 0 ? currentItemIndex - loadMoreFF : 0;

    const end =
      currentItemIndex + loadMoreFF < refs.length
        ? currentItemIndex + loadMoreFF
        : refs.length;

    const toLoad = [
      ...refs.slice(start, currentItemIndex),
      ...refs.slice(currentItemIndex + 1, end + 1),
    ];

    if (toLoad.length > 0) {
      addItems(
        toLoad,
        setNavState(() => ({
          start,
          end,
        })),
      );
    }
  };

  /**
   * @param itemRefs {Array}
   * @param afterLoad {Function}
   */
  const addItems = (itemRefs = [], afterLoad = () => {}) => {
    app.addItems({
      items: itemRefs,
      removePreviousItems: false,
    });

    const stopNavigation = (e) => {
      e.preventDefault();
    };
    app.once('item:load', afterLoad);
    app.once('item:beforeunload', stopNavigation);
  };

  const gotoQuestion = (itemRef, itemIndex, isNext) => {
    setIsNavDisabled(true);

    if (loadMoreFF) {
      itemObject.goto(itemRef).then(() => {
        typeSetOnLearnosityItem(itemRef);
      });

      const nextIndexToLoad =
        navState.end < learnosityItems.length &&
        navState.end - itemIndex < loadMoreFF &&
        navState.end + 1;

      const previousIndexToLoad =
        navState.start > 0 &&
        itemIndex - navState.start < loadMoreFF &&
        navState.start - 1;

      if (nextIndexToLoad && isNext) {
        const itemToLoad = learnosityItems[nextIndexToLoad];
        itemToLoad &&
          addItems([itemToLoad], () => {
            setIsNavDisabled(false);
            setNavState((state) => ({
              ...state,
              end: nextIndexToLoad,
            }));
          });
      }

      if (previousIndexToLoad && !isNext) {
        const itemToLoad = learnosityItems[previousIndexToLoad];
        itemToLoad &&
          addItems([itemToLoad], () => {
            setIsNavDisabled(false);
            setNavState((state) => ({
              ...state,
              start: previousIndexToLoad,
            }));
          });
      }

      if ((isNext && !nextIndexToLoad) || (!isNext && !previousIndexToLoad)) {
        setIsNavDisabled(false);
      }
    } else if (!(itemRef in app.getItems())) {
      app.once('item:load', () => setIsNavDisabled(false));
      app.addItems({
        items: [itemRef],
        removePreviousItems: false,
      });
    } else {
      itemObject.goto(itemRef);
    }

    if (itemIndex >= 0) {
      // trigger a callback to update preview item on ItemWithMetadataTable
      onItemChange(items[itemIndex].item_id);
    }
    setCurrentPreviewIndex(itemIndex);
  };

  /**
   * Handles next button click
   * @param {object} e The object click event
   * */
  function onNextClick() {
    const learnosityItemsLength = learnosityItems.length;
    const tempIndex = currentPreviewIndex + 1;
    const { subject } = params;
    const { query } = location;
    const shouldGetNextPage =
      tempIndex >= learnosityItemsLength && hasMorePages;

    if (shouldGetNextPage) {
      // push the router to create tab so we can fetch the next set of items
      const page = parseInt(query.page, 10);
      query.page = Number.isNaN(page) ? 1 : page + 1;

      const forcePreview = query['force-preview'];

      if (!forcePreview) {
        query['force-preview'] = true;
        query.item = 'first';
      } else if (forcePreview === 'null') {
        query['force-preview'] = true;
      }

      router.push({
        pathname: `/${subject}${pathname}`,
        query,
      });
    } else {
      const lRef = learnosityItems[tempIndex];
      gotoQuestion(lRef, tempIndex, true);
    }
  }

  /**
   * Handles prev button click
   * @param {object} e The object click event
   * */
  function onPrevClick() {
    const { query } = location;
    const { subject } = params;
    const tempIndex = currentPreviewIndex - 1;

    const shouldGetPrevPage = tempIndex <= -1 && currentPage !== 0;

    if (shouldGetPrevPage) {
      if (query.page > 0) {
        query.page = parseInt(query.page, 10) - 1;
        const forcePreview = query['force-preview'];
        if (!forcePreview || forcePreview === 'null') {
          query['force-preview'] = true;
        }
        query.item = 'last';

        router.push({
          pathname: `/${subject}${pathname}`,
          query,
        });
      }
    } else {
      const lRef = learnosityItems[tempIndex];
      gotoQuestion(lRef, tempIndex);
    }
  }

  function getIndexByReference(reference) {
    return findIndex(learnosityItems, (ref) => ref === reference);
  }

  function getInitialItemRefFromRoute() {
    const itemId = parseInt(params.previewId, 10);
    const item = items.find((item) => parseInt(item.item_id, 10) === itemId);
    return item?.learnosity_item_reference;
  }

  function updateRouteUrl(reference) {
    const { query } = location;
    const { subject } = params;
    const currentItem = find(items, {
      learnosity_item_reference: reference,
    });
    if (currentItem) {
      let pathUrl;
      if (pathname) {
        pathUrl = `/${subject}${pathname}${currentItem.item_id}`;
      } else {
        pathUrl = `/${subject}/${qbPathQuestions}/preview/item/${currentItem.item_id}`;
      }
      const queryString = Object.keys(query)
        .map((key) => `${key}=${query[key]}`)
        .join('&');
      // we can not use router.push here as it will be initializing Learnosity player on every update
      const newUrl = `${FYM_FRONTEND_URL}${pathUrl}${
        queryString ? `?${queryString}` : ''
      }`;
      window.history.replaceState({}, '', newUrl);
      onUrlUpdate(currentItem, newUrl);
    }
  }

  /**
   * Calculate the countOf for the navigation.
   * if we are at the first page just add 1 to our currentPreviewIndex
   * esle if we still have more page to navigate to
   * countOf = (currentPreviewIndex + itemsLength) * currentPage
   * otherwise if we are at the last page
   * countOf = (total - itemsLength) + currentPreviewIndex.
   * NOTE total is the total number of filtered items
   * itemsLength is the total number of items requested for this
   * page by default 30 items perpage
   **/
  function calculatePageCount() {
    const itemsLength = items.length;
    const page = currentPage ? currentPage + 1 : 1;
    const itemIndex = currentPreviewIndex + 1;
    let countOf = itemIndex;
    if (page > 1) {
      countOf =
        page * itemsPerPage -
        itemsLength +
        itemIndex -
        (itemsPerPage - itemsLength);
    }

    const currentPageCount = `${countOf}\u00A0\u00A0of\u00A0\u00A0${total}`; // unicode string for extra space
    setPageCount(currentPageCount);
  }

  const { query } = location;
  const hasPrevButton =
    !isNavDisabled && (query.page > 0 || currentPreviewIndex > 0);
  const hasNextButton =
    !isNavDisabled &&
    (hasMorePages || currentPreviewIndex + 1 < learnosityItems.length) &&
    currentPreviewIndex >= -1;

  const navigation = (
    <div className="QuestionNavigation">
      <div className="question-navigation flex items-center">
        <Button
          isPrimary
          isIcon
          disabled={!hasPrevButton}
          onClick={onPrevClick}
          className="prev-page-button"
          aria-label="Go to previous question"
        >
          <Icon name="chevron_left" size="15px" />
        </Button>
        <div className="page-count mx-4" data-test-page-count>
          {pageCount}
        </div>
        <Button
          isPrimary
          isIcon
          disabled={!hasNextButton}
          className="next-page-button"
          onClick={onNextClick}
          aria-label="Go to next question"
        >
          <Icon name="chevron_right" size="15px" />
        </Button>
      </div>
    </div>
  );

  return (
    <Fragment>
      <div className="absolute z-max rounded-full border border-solid border-gray-300 p-3 flex bg-white right-12 top-6 QuestionNavigationTop">
        {navigation}
      </div>
      <div className="absolute z-max rounded-full border border-solid border-gray-300 p-3 flex bg-white right-12 bottom-12">
        {navigation}
      </div>
    </Fragment>
  );
};
QuestionNavigation.propTypes = {
  hasMorePages: PropTypes.bool.isRequired,
  items: PropTypes.array,
  currentPage: PropTypes.number,
  total: PropTypes.number,
  pathname: PropTypes.string,
};

QuestionNavigation.defaultProps = {
  currentPage: 0,
  items: [],
  total: 0,
  pathname: '/question_bank/questions/',
};
export default QuestionNavigation;
